import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Image as ImageIcon, Loader2, X } from "lucide-react";

type Props = {
  attachmentId: number | null;
  filename: string;
  loadImage: (attachmentId: number) => Promise<Blob>;
  onClose: () => void;
};

export function ChatImagePreviewModal({
  attachmentId,
  filename,
  loadImage,
  onClose,
}: Props) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (attachmentId === null || attachmentId <= 0) {
      setImageUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let active = true;
    let objectUrl: string | null = null;

    setImageUrl(null);
    setLoading(true);
    setError(null);

    void loadImage(attachmentId)
      .then((blob) => {
        if (!active) return;

        objectUrl = window.URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch(() => {
        if (!active) return;
        setError("No se pudo cargar la vista preliminar de la imagen.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachmentId, loadImage]);

  useEffect(() => {
    if (attachmentId === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [attachmentId, onClose]);

  if (attachmentId === null || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[160] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Vista preliminar de ${filename}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-white/15 bg-white shadow-[0_40px_120px_rgba(15,23,42,.38)]">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
              <ImageIcon size={17} />
            </span>
            <div className="min-w-0">
              <div className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">
                Vista preliminar
              </div>
              <div className="truncate text-sm font-black text-slate-950">{filename}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
            title="Cerrar vista preliminar"
            aria-label="Cerrar vista preliminar"
          >
            <X size={17} />
          </button>
        </div>

        <div className="flex min-h-[260px] flex-1 items-center justify-center overflow-auto bg-[linear-gradient(135deg,#f8fafc,#eef7f3)] p-5">
          {loading && (
            <div className="flex items-center gap-2 rounded-full border border-emerald-100 bg-white px-4 py-2 text-sm font-black text-emerald-700 shadow-sm">
              <Loader2 size={16} className="animate-spin" />
              Cargando imagen...
            </div>
          )}

          {!loading && error && (
            <div className="max-w-md rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-center text-sm font-bold text-rose-700">
              {error}
            </div>
          )}

          {!loading && !error && imageUrl && (
            <img
              src={imageUrl}
              alt={filename}
              className="max-h-[76vh] max-w-full rounded-2xl object-contain shadow-[0_20px_60px_rgba(15,23,42,.16)]"
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
